import React from 'react';
import { Avatar } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectCurrentSpace } from '@/store/selectors/spaceSelectors';
import { TeamRole } from '@/store/slices/spaceSlice';
import { AccountListItem, AccountType } from '@/services/types';
import UserAvatarIcon from '@/assets/icons/user-avatar.svg?url';

/**
 * 「切换账户」二级下拉面板（V0.1，20261026 方案调整）
 *
 * 交互变更：不再原地替换浮层摘要区，改为自「切换账户」项右侧弹出的二级面板。
 * 内容按设计稿：个人账户组（含当前 ✓，支持从团队切回个人）→ 团队账户组（成员在前拥有者在后）。
 * 创建团队入口本期注释不展示（下次迭代再做）。
 */
interface SwitchAccountPanelProps {
  accountList: AccountListItem[];
  loading: boolean;
  /** 个人账户当前套餐名（如「标准会员」，无套餐则不拼接） */
  personalPlanName?: string;
  maskedMobile: string;
  onSwitch: (account: AccountListItem) => void;
}

/** 团队字母头像底色（按名称首字符稳定取色；索引 3 蓝 / 索引 4 青绿，对齐设计稿示例） */
const AVATAR_COLORS = ['#5B7CFA', '#E8734A', '#8A63D2', '#4C6FFF', '#1E9E77'];

const groupLabelStyle: React.CSSProperties = {
  padding: '8px 12px 4px',
  color: 'rgba(255,255,255,0.35)',
  fontSize: '12px',
  lineHeight: '16px',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background 0.15s ease',
};

const titleStyle: React.CSSProperties = {
  color: '#FFF',
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const dimStyle: React.CSSProperties = {
  color: 'rgba(255,255,255,0.4)',
  fontSize: '12px',
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
    <path
      d="M2.5 7.5L5.5 10.5L11.5 3.5"
      stroke="#FEAE45"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SwitchAccountPanel: React.FC<SwitchAccountPanelProps> = ({
  accountList,
  loading,
  personalPlanName,
  maskedMobile,
  onSwitch,
}) => {
  const { t } = useTranslation();
  const currentSpace = useSelector(selectCurrentSpace);

  const personal = accountList.find(a => a.account_type === AccountType.Personal);
  const teams = accountList.filter(a => a.account_type === AccountType.Team);

  const isCurrent = (account: AccountListItem) =>
    currentSpace
      ? account.user_id === currentSpace.teamId
      : account.account_type === AccountType.Personal;

  /** 头像：有 avatar 用图；个人账户兜底人形默认图标；团队按名称首字符生成字母头像 */
  const renderAvatar = (account: AccountListItem) => {
    const src = account.user_avatar || (account.account_type === AccountType.Personal ? UserAvatarIcon : '');
    if (src) {
      return (
        <Avatar size={32} src={src} style={{ flexShrink: 0, backgroundColor: 'rgba(255,255,255,0.1)' }} />
      );
    }
    const name = account.user_name || account.mobile || ' ';
    const ch = name.trim().charAt(0).toUpperCase() || '·';
    const color = AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];
    return (
      <Avatar size={32} style={{ backgroundColor: color, color: '#FFF', fontSize: 14, flexShrink: 0 }}>
        {ch}
      </Avatar>
    );
  };

  const renderRow = (account: AccountListItem, content: React.ReactNode) => {
    const current = isCurrent(account);
    return (
      <div
        key={account.user_id}
        onClick={() => {
          if (!current) onSwitch(account);
        }}
        style={{ ...rowStyle, background: current ? 'rgba(255,255,255,0.07)' : 'transparent', cursor: current ? 'default' : 'pointer' }}
        onMouseEnter={e => {
          if (!current) e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = current ? 'rgba(255,255,255,0.07)' : 'transparent';
        }}
      >
        {renderAvatar(account)}
        <div style={{ flex: 1, minWidth: 0 }}>{content}</div>
        {current && <CheckIcon />}
      </div>
    );
  };

  // const handleCreateTeam = () => {
  //   // 本期后端无创建团队接口（成员由后台添加），先提示即将上线
  //   message.info(t('space.createTeamComingSoon'));
  // };

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        width: '280px',
        maxHeight: '60vh',
        overflowY: 'auto',
        borderRadius: '12px',
        background: '#2B2B2B',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.08)',
        padding: '8px',
        boxSizing: 'border-box',
      }}
    >
      {loading && (
        <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
          {t('common.loading')}
        </div>
      )}

      {!loading && personal && (
        <>
          <div style={groupLabelStyle}>{t('space.personalGroup')}</div>
          {renderRow(
            personal,
            <div style={{ minWidth: 0 }}>
              <div style={titleStyle}>{maskedMobile || personal.user_name || t('user.defaultName')}</div>
              <div style={{ ...dimStyle, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {personalPlanName
                  ? `${t('space.personalAccount')} · ${personalPlanName}`
                  : t('space.personalAccount')}
              </div>
            </div>
          )}
        </>
      )}

      {!loading && teams.length > 0 && (
        <>
          <div style={{ ...groupLabelStyle, paddingTop: '12px' }}>{t('space.teamGroup')}</div>
          {teams.map(account =>
            renderRow(
              account,
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  fontSize: '13px',
                  lineHeight: '18px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <span style={titleStyle}>{account.user_name || account.mobile}</span>
                {/* 间隔符（20261029 主公定稿）：团队名与身份之间加「 · 」，与浮层摘要区身份行（团队名 · 角色）格式一致 */}
                <span style={{ ...dimStyle, flexShrink: 0 }}>
                  {` · ${
                    account.team_role === TeamRole.Owner
                      ? t('space.identityOwner')
                      : t('space.identityMember')
                  }`}
                </span>
              </div>
            )
          )}
        </>
      )}

      {/* 创建团队：本期注释不展示，下次迭代再做 */}
      {/* {!loading && (
        <button
          onClick={handleCreateTeam}
          style={{
            width: '100%',
            height: '44px',
            marginTop: '12px',
            background: 'transparent',
            border: '1px dashed rgba(255,255,255,0.25)',
            borderRadius: '8px',
            color: 'rgba(255,255,255,0.7)',
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          + {t('space.createTeam')}
        </button>
      )} */}
    </div>
  );
};

export default SwitchAccountPanel;
